// IAM observability — surfaces auth/registration failures in Sentry and forces
// full Session Replay on IAM flows, centralized at the API layer so every IAM
// handler is covered without per-component wiring.
//
// IAM failures are HANDLED non-2xx responses (not crashes), so they would never
// reach Sentry on their own. We capture them as warning (4xx) / error (5xx)
// captureMessage events with a stable fingerprint, and PRE-SCRUB credentials so
// nothing sensitive leaves the browser.
import * as Sentry from "@sentry/react";

// IAM endpoint path stems (token-free). A request whose path equals a stem or
// starts with `<stem>/` (i.e. a tokenized route like lawfirm-user-register/<token>)
// is treated as IAM. Mirrors the backend IAM_VIEW_NAMES set.
const IAM_STEMS = [
  "login", "token/refresh", "logout", "2fa-verify",
  "register", "first-time-register",
  "register-lawfirm-guardian", "lawfirm-user-register", "invite-lawfirm-user",
  "register-internal-user", "carrier-team-member/create",
  "forgot-password", "reset-password", "update-user-details",
  "forgot-username", "forgot-username-verify",
  "email-exists", "accounts-by-email", "username-unique",
  "lawfirms-locations", "get-locations-by-lawfirm",
  "register-professional-guardian", "register-by-invite",
  "pg-contacts-register", "pg-contacts/profile-completion", "accept-invite",
];

const SENSITIVE_KEY = /(password|secret|token|api[_-]?key|otp|ssn|fein|card|cvv|cvc)/i;

const UUID_SEGMENT = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
const SLUG_SEGMENT = /^[a-z]+(?:-[a-z]+)*$/; // lowercase hyphen-words = route name, not a token

function isTokenSegment(seg) {
  if (UUID_SEGMENT.test(seg)) return true;
  return seg.length >= 20 && !SLUG_SEGMENT.test(seg);
}

// Drop the query string (may carry ?token=/?email=) and redact token-like path
// segments (UUIDs, long opaque strings). Works on absolute URLs and bare paths.
export function redactUrl(url) {
  if (typeof url !== "string") return url;
  try {
    const hasScheme = /^[a-z][a-z0-9+.-]*:\/\//i.test(url);
    const parsed = new URL(url, hasScheme ? undefined : "http://_local_");
    const path = parsed.pathname
      .split("/")
      .map((seg) => (isTokenSegment(seg) ? "<redacted>" : seg))
      .join("/");
    return hasScheme ? `${parsed.protocol}//${parsed.host}${path}` : path;
  } catch {
    return url.split("?")[0];
  }
}

function redactCrumbUrls(data) {
  if (!data || typeof data !== "object") return data;
  const out = { ...data };
  for (const key of ["url", "from", "to"]) {
    if (typeof out[key] === "string") out[key] = redactUrl(out[key]);
  }
  return out;
}

// Sentry before_send backstop: scrub the auto-attached page URL, transaction
// name, and breadcrumb URLs so tokenized routes (/lawfirm-user-register/<token>,
// /register-by-invite/<token>, ...) never leak the token to Sentry. Never throws.
export function scrubSentryEvent(event, hint) {
  try {
    if (event && event.request && typeof event.request.url === "string") {
      event.request.url = redactUrl(event.request.url);
    }
    if (event && typeof event.transaction === "string") {
      event.transaction = redactUrl(event.transaction);
    }
    if (event && Array.isArray(event.breadcrumbs)) {
      event.breadcrumbs = event.breadcrumbs.map((c) =>
        c && c.data ? { ...c, data: redactCrumbUrls(c.data) } : c
      );
    }
  } catch {
    /* never break the SDK pipeline */
  }
  return event;
}

// Sentry before_breadcrumb backstop: scrub URLs on navigation/xhr/fetch crumbs.
export function scrubSentryBreadcrumb(crumb, hint) {
  try {
    if (crumb && crumb.data) crumb.data = redactCrumbUrls(crumb.data);
  } catch {
    /* never break the SDK pipeline */
  }
  return crumb;
}

function normalizePath(endpoint) {
  if (typeof endpoint !== "string") return "";
  return endpoint.split("?")[0].replace(/^\/+/, "").replace(/\/+$/, "");
}

// Returns the matched token-free stem (stable Sentry label), or null if not IAM.
export function iamRouteLabel(endpoint) {
  const path = normalizePath(endpoint);
  for (const stem of IAM_STEMS) {
    if (path === stem || path.startsWith(stem + "/")) return stem;
  }
  return null;
}

export function isIamEndpoint(endpoint) {
  return iamRouteLabel(endpoint) !== null;
}

// Correlation id stamped on the request (X-Request-ID header) AND tagged on the
// Sentry event, so a frontend IAM event ties to the backend event + api_logs row
// for the same request. The Azure gateway preserves a client-sent X-Request-ID.
// Never throws.
export function newRequestId() {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
  } catch {
    /* fall through to the manual id */
  }
  return "fe-" + Date.now().toString(36) + "-" + Math.random().toString(36).slice(2, 10);
}

// Recursively redact credential keys, and mask emails to domain BEFORE sending to
// Sentry (external SaaS). Emails are kept RAW in api_logs (internal triage store),
// but never shipped raw to a third party — mask here.
export function scrubExtras(value) {
  if (Array.isArray(value)) return value.map(scrubExtras);
  if (value && typeof value === "object") {
    const out = {};
    for (const [k, v] of Object.entries(value)) {
      if (SENSITIVE_KEY.test(k)) out[k] = "[REDACTED]";
      else if (/email|username|recovery_email/i.test(k) && typeof v === "string" && v.includes("@"))
        out[k] = "*@" + v.split("@").pop();
      else out[k] = scrubExtras(v);
    }
    return out;
  }
  return value;
}

function getReplayInstance() {
  try {
    if (typeof Sentry.getReplay === "function") return Sentry.getReplay();
    const client = Sentry.getClient && Sentry.getClient();
    return (client && client.getIntegrationByName && client.getIntegrationByName("Replay")) || null;
  } catch {
    return null;
  }
}

// Force full-session replay for IAM flows. Replay otherwise samples at
// replaysSessionSampleRate and only captures on ERROR — IAM failures are
// warnings, so without this an IAM session would usually not be recorded.
// Idempotent + never throws.
export function forceIamReplay() {
  try {
    const replay = getReplayInstance();
    if (!replay) return;
    // Mode-aware: under prod sampling (replaysSessionSampleRate=0.1) most sessions
    // record in BUFFER mode, which only sends on an ERROR. IAM failures are
    // warnings, so a buffered replay would never reach Sentry — flush() sends the
    // session leading up to the failure. If already a full session, leave it; if
    // not recording at all, start one.
    const mode = typeof replay.getRecordingMode === "function" ? replay.getRecordingMode() : undefined;
    if (mode === "session") return;                                   // already recording continuously
    if (mode === "buffer" && typeof replay.flush === "function") {
      replay.flush();                                                 // promote buffered recording → sent replay
      return;
    }
    if (typeof replay.start === "function") replay.start();           // not recording → start a session
  } catch {
    /* observability must never break the app */
  }
}

// Emit a scoped, scrubbed Sentry event for an IAM non-2xx response, and ensure
// the session is recorded. Never throws.
export function captureIamFailure(endpoint, status, reason, requestId) {
  try {
    const label = iamRouteLabel(endpoint);
    if (!label || !status || status < 400) return;
    forceIamReplay();
    const level = status >= 500 ? "error" : "warning";
    Sentry.captureMessage(`IAM ${level}: ${label}`, {
      level,
      fingerprint: ["iam", label, String(status)],
      // request_id ties this to the backend event + api_logs row (omitted if absent)
      tags: { iam_event: label, iam_status: status, request_id: requestId },
      extra: { endpoint: label, status, reason: scrubExtras(reason) },
    });
  } catch {
    /* observability must never break the app */
  }
}
